// src/hooks/useWhitepaperPrompt.ts
//
// Hook qui orchestre l'affichage automatique d'une modale livre blanc :
// - Trigger A : exit-intent (souris quitte par le bord supérieur du viewport)
// - Trigger B : scroll dépasse `scrollThreshold` (60 % par défaut)
// - Trigger C : timer fallback `idleAfterMs` (30 s par défaut)
// - Frequency cap localStorage :
//     - dismiss → silence `dismissCooldownDays` jours (7 par défaut)
//     - downloaded → silence permanent
// - Mobile : pas d'auto-trigger (intrusif). L'utilisateur peut ouvrir via un bouton inline.
//
// Usage :
//   const { isOpen, open, close, markDownloaded } = useWhitepaperPrompt({
//     slug: 'chiffres-cles-recrutement-2026',
//   });
//   return (
//     <>
//       <WhitepaperModal open={isOpen} onClose={close} onDownloaded={markDownloaded} ... />
//       <button onClick={open}>Télécharger le livre blanc</button>
//     </>
//   );

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseWhitepaperPromptOptions {
  slug: string;
  scrollThreshold?: number; // 0..1 (default 0.6 = 60%)
  idleAfterMs?: number; // default 30000 (30s)
  dismissCooldownDays?: number; // default 7
  /** Désactive l'auto-trigger (mais open() manuel reste possible). */
  disableAutoTrigger?: boolean;
}

const MOBILE_BREAKPOINT = 768; // < md

function lsKey(slug: string, suffix: 'dismissed' | 'downloaded') {
  return `whitepaper:${slug}:${suffix}`;
}

export function useWhitepaperPrompt({
  slug,
  scrollThreshold = 0.6,
  idleAfterMs = 30000,
  dismissCooldownDays = 7,
  disableAutoTrigger = false,
}: UseWhitepaperPromptOptions) {
  const [isOpen, setIsOpen] = useState(false);
  const triggeredRef = useRef(false);

  const open = useCallback(() => {
    triggeredRef.current = true;
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    // Marque dismiss → silence X jours
    try {
      localStorage.setItem(lsKey(slug, 'dismissed'), String(Date.now()));
    } catch {
      /* localStorage indisponible — pas grave */
    }
    setIsOpen(false);
  }, [slug]);

  const markDownloaded = useCallback(() => {
    try {
      localStorage.setItem(lsKey(slug, 'downloaded'), '1');
    } catch {
      /* noop */
    }
    // Note : on ne ferme PAS automatiquement, le success state reste affiché.
  }, [slug]);

  useEffect(() => {
    if (disableAutoTrigger) return;
    if (typeof window === 'undefined') return;

    // Skip sur mobile (intrusif)
    if (window.innerWidth < MOBILE_BREAKPOINT) return;

    // Skip si dépendant d'une condition localStorage
    try {
      if (localStorage.getItem(lsKey(slug, 'downloaded'))) return;
      const dismissedAt = localStorage.getItem(lsKey(slug, 'dismissed'));
      if (dismissedAt) {
        const age = Date.now() - parseInt(dismissedAt, 10);
        const cooldownMs = dismissCooldownDays * 24 * 60 * 60 * 1000;
        if (age < cooldownMs) return;
      }
    } catch {
      /* localStorage indispo → on continue (mieux que silence) */
    }

    const fire = () => {
      if (triggeredRef.current) return;
      triggeredRef.current = true;
      setIsOpen(true);
    };

    // Trigger A — exit-intent (sortie par le haut du viewport)
    const onMouseLeave = (e: MouseEvent) => {
      if (triggeredRef.current) return;
      // clientY <= 0 = quitte par le haut (onglet/URL bar)
      if (e.clientY > 0) return;
      fire();
    };

    // Trigger B — scroll dépasse threshold
    const onScroll = () => {
      if (triggeredRef.current) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const winH = window.innerHeight || document.documentElement.clientHeight;
      const docH = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
      );
      if (docH <= winH) return; // page trop courte
      const ratio = (scrollTop + winH) / docH;
      if (ratio >= scrollThreshold) fire();
    };

    // Trigger C — timer fallback (engagement minimal)
    const timer = window.setTimeout(fire, idleAfterMs);

    document.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      document.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(timer);
    };
  }, [slug, scrollThreshold, idleAfterMs, dismissCooldownDays, disableAutoTrigger]);

  return { isOpen, open, close, markDownloaded };
}
